import "./Bundles.scss";

import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import Menu from "../../components/Menu/Menu";
import { MenuProps } from "../../components/Menu/MenuTypes";
import { defaultMenuProps } from "../../constants/constants";
import { getApiBundles, deleteApiBundle, toggleApiBundle } from "../../services/apiBundle";
import { types } from "../../constants/constants";
import { TableProps } from "../../components/Table/TableTypes";
import Table from "../../components/Table/Table";
import Button from "../../components/Button/Button";
import Toast from "../../components/Toast/Toast";
import { useToast } from "../../hooks/useToast";
import dayjs from "dayjs";
import 'dayjs/locale/es';

const Bundles = (): React.ReactElement => {
  const navigate = useNavigate();
  const [menuProps] = useState<MenuProps>(defaultMenuProps);
  const [bundles, setBundles] = useState<TableProps['items']>([]);
  const [limit] = useState<number>(types.PAGE_SIZE);
  const [offset, setOffset] = useState<number>(0);
  const [totalBundles, setTotalBundles] = useState<number>(0);
  const [actions, setActions] = useState<TableProps["actions"]>([]);
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const { show, message, type, showToast, hideToast } = useToast();

  useEffect(() => {
    setActions([
      { name: 'Editar', onClick: editAction, icon: 'pencil' },
      { name: 'Activar/Desactivar', onClick: toggleAction, icon: 'eye' },
      { name: 'Borrar', onClick: deleteAction, icon: 'deleteIcon' }
    ]);
  }, []);

  useEffect(() => {
    getBundles();
  }, [sortBy, sortDirection]);

  const getBundles = async (): Promise<void> => {
    getApiBundles({ limit, offset, sortBy, sortDirection })
      .then(async (response: any) => {
        setTotalBundles(response.total);
        const results: TableProps['items'] = response.bundles && response.bundles.map((bundle: any, index: number) => [
          { name: '#', value: index + 1 },
          { name: 'Id', value: bundle._id },
          { name: 'Título', value: bundle.title, sortable: true, index: 'title' },
          { name: 'Slug', value: bundle.slug },
          { name: 'Precio', value: bundle.price ? `${bundle.price}€` : '-', sortable: true, index: 'price' },
          { name: 'Nº Cursos', value: bundle.courses?.length || 0 },
          { name: 'Estado', value: bundle.active ? 'Activo' : 'Inactivo', className: bundle.active ? 'text-success' : 'text-danger' },
          { name: 'Creado', value: dayjs(bundle.created_at).format('DD/MM/YYYY'), sortable: true, index: 'created_at', className: 'text-center' }
        ]);
        setBundles(results);
      })
      .catch((error: any) => {
        console.log(error.message);
        setBundles([]);
      });
  };

  const loadMoreBundles = async (): Promise<void> => {
    const offsetPage = offset + limit;
    getApiBundles({ limit, offset: offsetPage, sortBy, sortDirection })
      .then(async (response: any) => {
        const results: TableProps['items'] = response.bundles && response.bundles.map((bundle: any, index: number) => [
          { name: '#', value: index + offsetPage + 1 },
          { name: 'Id', value: bundle._id },
          { name: 'Título', value: bundle.title, sortable: true, index: 'title' },
          { name: 'Slug', value: bundle.slug },
          { name: 'Precio', value: bundle.price ? `${bundle.price}€` : '-', sortable: true, index: 'price' },
          { name: 'Nº Cursos', value: bundle.courses?.length || 0 },
          { name: 'Estado', value: bundle.active ? 'Activo' : 'Inactivo', className: bundle.active ? 'text-success' : 'text-danger' },
          { name: 'Creado', value: dayjs(bundle.created_at).format('DD/MM/YYYY'), sortable: true, index: 'created_at', className: 'text-center' }
        ]);
        setOffset(offsetPage);
        setBundles(bundles.concat(results));
      })
      .catch((error: any) => {
        console.log(error.message);
      });
  };

  const editAction = (id: string): void => navigate(`/bundles/${id}`);

  const toggleAction = async (id: string): Promise<void> => {
    try {
      showToast('Cambiando estado...', 'info');
      await toggleApiBundle(id);
      setOffset(0);
      // Recarga con offset 0
      getApiBundles({ limit, offset: 0, sortBy, sortDirection })
        .then(async (response: any) => {
          setTotalBundles(response.total);
          const results: TableProps['items'] = response.bundles && response.bundles.map((bundle: any, index: number) => [
            { name: '#', value: index + 1 },
            { name: 'Id', value: bundle._id },
            { name: 'Título', value: bundle.title, sortable: true, index: 'title' },
            { name: 'Slug', value: bundle.slug },
            { name: 'Precio', value: bundle.price ? `${bundle.price}€` : '-', sortable: true, index: 'price' },
            { name: 'Nº Cursos', value: bundle.courses?.length || 0 },
            { name: 'Estado', value: bundle.active ? 'Activo' : 'Inactivo', className: bundle.active ? 'text-success' : 'text-danger' },
            { name: 'Creado', value: dayjs(bundle.created_at).format('DD/MM/YYYY'), sortable: true, index: 'created_at', className: 'text-center' }
          ]);
          setBundles(results);
          showToast('Estado actualizado correctamente', 'success');
        })
        .catch((error: any) => {
          console.log(error.message);
          showToast('Error al actualizar el estado', 'error');
        });
    } catch (error: any) {
      console.error('Toggle error:', error);
      showToast('Error al cambiar el estado del bundle', 'error');
    }
  };

  const deleteAction = async (id: string): Promise<void> => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este bundle?')) return;
    try {
      await deleteApiBundle(id);
      getBundles();
    } catch (error: any) {
      alert('Error al eliminar el bundle');
    }
  };

  const doSortBy = (field: string): void => {
    setOffset(0);
    setSortBy(field);
    setSortDirection(sortDirection === 1 ? -1 : 1);
  };

  return (
    <React.Fragment>
      <Toast show={show} message={message} type={type} onClose={hideToast} />
      <div id="menu">
        <Menu items={menuProps.items} />
      </div>
      <div id="app-content">
        <header className="table-header mb-4">
          <h1>Bundles</h1>
          <Button className={'btn-orangebg ms-auto me-3'} value={'Añadir bundle'} onClick={() => navigate('/bundles/crear')} />
          <span>Total registros: {totalBundles}</span>
        </header>
        {bundles && bundles.length
          ? <Table items={bundles} actions={actions} doSortBy={doSortBy} />
          : <p>No hay bundles</p>
        }
        {totalBundles > bundles.length &&
          <div className="row mt-4">
            <div className="col-12 col-md-4 offset-md-4">
              <Button className={'btn-white m-auto'} value={'Cargar más'} onClick={loadMoreBundles} />
            </div>
          </div>
        }
      </div>
    </React.Fragment>
  );
};

export default Bundles;
